import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/api";
import {
  Gift, PartyPopper, MapPin, Star, DollarSign, Loader2,
  ChevronRight, Sparkles, ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CATEGORY_COLORS = {
  Restaurant: "bg-orange-50 text-orange-600",
  "Fast Food": "bg-red-50 text-red-600",
  Coffee: "bg-amber-50 text-amber-700",
  "Bakery / Dessert": "bg-pink-50 text-pink-600",
  Cosmetics: "bg-fuchsia-50 text-fuchsia-600",
  Clothing: "bg-violet-50 text-violet-600",
  Entertainment: "bg-sky-50 text-sky-600",
  Fitness: "bg-emerald-50 text-emerald-600",
  "Ice Cream / Frozen Yogurt": "bg-cyan-50 text-cyan-600",
};

export default function BirthdayPlanViewPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/birthday-plans/${planId}`);
        if (!res.ok) throw new Error("Not found");
        setPlan(await res.json());
      } catch { setError(true); }
      finally { setLoading(false); }
    })();
  }, [planId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  if (error || !plan) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="text-center">
        <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h1 className="text-lg font-bold text-slate-700">Plan not found</h1>
        <p className="text-sm text-slate-400 mt-1">This birthday plan may have been removed.</p>
        <button onClick={() => navigate("/onboarding")} className="mt-4 px-5 py-2.5 bg-pink-500 text-white text-sm font-semibold rounded-xl hover:bg-pink-600">
          Sign Up for Free
        </button>
      </div>
    </div>
  );

  const month = MONTHS[plan.birthday_month - 1] || "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-slate-50" data-testid="birthday-plan-view">
      {/* Hero */}
      <div className="px-5 pt-10 pb-6 text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <PartyPopper className="w-10 h-10 text-pink-500" />
        </motion.div>
        <motion.h1 initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="text-2xl font-bold text-slate-800" data-testid="plan-friend-name">
          {plan.friend_name}'s BirthdayDeaLight
        </motion.h1>
        <motion.p initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-sm text-slate-500 mt-1">
          {month} Birthday · Curated by {plan.creator_name}
        </motion.p>
        <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="flex items-center justify-center gap-4 mt-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-pink-600">{plan.offer_count}</p>
            <p className="text-[10px] text-slate-400">Deals</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600">${plan.total_value}</p>
            <p className="text-[10px] text-slate-400">Total Value</p>
          </div>
        </motion.div>
      </div>

      {/* Offer list */}
      <div className="px-4 pb-8 space-y-2">
        {plan.offers?.map((offer, idx) => {
          const catColor = CATEGORY_COLORS[offer.category] || "bg-slate-50 text-slate-600";
          return (
            <motion.div
              key={offer.business_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx }}
              className="bg-white rounded-xl border border-slate-100 p-3.5 shadow-sm"
              data-testid={`plan-offer-${offer.business_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 border border-slate-100 flex items-center justify-center">
                  {offer.logo_url ? (
                    <img src={offer.logo_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <Gift className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{offer.name}</h3>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${catColor}`}>{offer.category}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{offer.offer_title}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-base font-bold text-emerald-600">${offer.value}</p>
                  {offer.signup_days > 0 && (
                    <p className="text-[9px] text-amber-600">Sign up {offer.signup_days}d early</p>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* CTA for non-logged-in users */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur-md border-t border-slate-100 px-4 py-4">
        <button
          onClick={() => navigate("/onboarding")}
          className="w-full py-3.5 bg-pink-500 text-white text-sm font-bold rounded-xl hover:bg-pink-600 flex items-center justify-center gap-2 shadow-lg shadow-pink-200"
          data-testid="plan-signup-btn"
        >
          <Sparkles className="w-4 h-4" /> Claim These Deals — Sign Up Free
        </button>
        <p className="text-[10px] text-slate-400 text-center mt-2">Track, claim & never miss a birthday deal</p>
      </div>
    </div>
  );
}
